import React, { useState } from 'react';
import { getAuth, signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { BoltIcon } from '@heroicons/react/24/solid';

import SearchModal from './components/ModalSearch';
import UserMenu from './components/DropdownProfile';
import { useUser } from './userContext';
import { useCreditModal } from './components/CreditModalContext';

function Header({
  sidebarOpen,
  setSidebarOpen,
  variant = 'default',
}) {
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const auth = getAuth();
  const navigate = useNavigate();
  const { gameCredits, freeSlotInUse } = useUser();
  const { openCreditModal } = useCreditModal();
  // Credits indicator: count of publishes ready to use (paid credits + the
  // one free slot if unused). Empty state flips to amber "Power up" to signal
  // urgency without shouting.
  const totalPublishes = (gameCredits || 0) + (!freeSlotInUse ? 1 : 0);
  const isEmpty = totalPublishes === 0;
  const creditsLabel = isEmpty ? 'Buy credits' : `${totalPublishes} ready`;

  const handleSignOut = () => {
    signOut(auth).then(() => {
      navigate('/login');
    }).catch((error) => {
      console.error('Signout error', error);
    });
  };

  return (
    <>
      <header className="sticky top-0 z-30 gch-header">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <button
                className="lg:hidden p-2.5 rounded-[var(--admin-card-radius)] transition-all duration-200 font-semibold gch-text-secondary gch-input-ghost hover:text-white"
                aria-controls="sidebar"
                aria-expanded={sidebarOpen}
                onClick={(e) => { e.stopPropagation(); setSidebarOpen(!sidebarOpen); }}
              >
                <span className="sr-only">Open sidebar</span>
                <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <rect x="4" y="5" width="16" height="2" rx="1" />
                  <rect x="4" y="11" width="16" height="2" rx="1" />
                  <rect x="4" y="17" width="16" height="2" rx="1" />
                </svg>
              </button>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <div>
                <button
                  className={`w-11 h-11 flex items-center justify-center rounded-[var(--admin-card-radius)] transition-all duration-200 font-semibold ${
                    searchModalOpen
                      ? 'bg-[var(--admin-cta-solid)] text-white border-2 border-[var(--admin-cta-border)]'
                      : 'gch-input-ghost gch-text-secondary hover:text-white'
                  }`}
                  onClick={(e) => { e.stopPropagation(); setSearchModalOpen(true); }}
                  aria-controls="search-modal"
                >
                  <span className="sr-only">Search</span>
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21.71 20.29L18 16.61A9 9 0 1 0 16.61 18l3.68 3.68a1 1 0 0 0 1.42 0 1 1 0 0 0 0-1.39zM11 18a7 7 0 1 1 7-7 7 7 0 0 1-7 7z"/>
                  </svg>
                </button>
                <SearchModal id="search-modal" searchId="search" modalOpen={searchModalOpen} setModalOpen={setSearchModalOpen} />
              </div>
              <button
                type="button"
                onClick={() => openCreditModal()}
                aria-label={isEmpty ? 'No publishes ready — buy credits' : `${totalPublishes} ${totalPublishes === 1 ? 'publish' : 'publishes'} ready`}
                title={creditsLabel}
                className={`group inline-flex items-center gap-1.5 px-3 h-9 rounded-[var(--admin-card-radius)] text-xs font-semibold whitespace-nowrap border transition-all duration-200 ${
                  isEmpty
                    ? 'border-amber-500/40 text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 hover:text-amber-200'
                    : 'border-emerald-400/30 text-emerald-200 bg-emerald-500/10 hover:bg-emerald-500/15 hover:text-emerald-100'
                }`}
              >
                <BoltIcon className={`w-4 h-4 shrink-0 ${isEmpty ? 'text-amber-400' : 'text-emerald-300 group-hover:text-emerald-200'}`} />
                <span>{creditsLabel}</span>
              </button>
              <div className="hidden sm:block w-0.5 h-8 rounded-full bg-white/10" />
              <UserMenu align="right" handleSignOut={handleSignOut} />
            </div>
          </div>
        </div>
      </header>

    </>
  );
}

export default Header;
